import React from 'react'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'

import { buildLocation } from 'lib/utils/page'
import { getSearch } from 'src/effects/Search/selectors'
import { scrollToTop } from 'client/utils'
import { routes } from 'src/constants/routes'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { usePage } from 'src/hooks/usePage'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { Button } from '@s4l/ds'
import IconMap from 'client/icons/Map'
import ChevronRight from 'client/icons/ChevronRight'
import { useS4LContext } from '@s4l/react-context'
import MapBanner from '../Banner'

interface MapLinkProps {
  propertyId?: number
  variant?: 'filter-button' | 'link' | 'map-button'
}

const MapLink = ({ propertyId, variant = 'link' }: MapLinkProps) => {
  const { i18n } = useS4LContext()
  const router = useRouter()
  const { pageId } = usePage()
  const searchParams = useSelector(getSearch)
  const { sendEvent } = useKafkaEvent({ page: pageId, eventName: 'show_on_map' })

  function getDestinationUrl(query: Record<string, string | string[]>) {
    if (propertyId) {
      return buildLocation({
        page: routes.map.page,
        path: { country: searchParams.country, type: searchParams.type, slug: searchParams.slug },
        query: { ...query, highlightedProperty: `${propertyId}` },
      })
    }

    return buildLocation({
      page: routes.map.page,
      path: searchParams,
      query,
    })
  }

  function goToMap(query = {}) {
    const destinationUrl = getDestinationUrl({ ...query, internal_navigation: 'true' })

    scrollToTop()

    router.push(destinationUrl.href, destinationUrl.as)
  }

  const { query } = useRouter()

  const handleClick = () => {
    sendEvent({ section: SECTIONS.HEADER })
    goToMap(query)
  }

  if (variant === 'filter-button') {
    return (
      <Button
        variant="outlined-rounded"
        onClick={handleClick}
        leadingIcon={<IconMap size={16} />}
        size="small"
        fullWidth
      >
        {i18n.formatMessage({ id: 'global.map' })}
      </Button>
    )
  }

  if (variant === 'map-button') {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div onClick={handleClick}>
        <MapBanner />
      </div>
    )
  }

  return (
    <Button
      variant="text-flat"
      onClick={handleClick}
      size="small"
      trailingIcon={<ChevronRight size={16} />}
    >
      {i18n.formatMessage({ id: 'global.show-on-map' })}
    </Button>
  )
}

export default MapLink
